import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { FormSection } from '@/common/components/Form/FormSection.tsx'
import { cn } from '@/utils/style.ts'
import { Trash2 } from 'lucide-react'
import { Button, Input, Select } from 'antd'
import { FormSwitch } from '@/common/components/Form/FormSwitch.tsx'
import { useFormSection } from '@/common/components/Form/useFormSection.tsx'
import { SimpleFormMessage } from '@/common/components/ui/form.tsx'
import React from 'react'
import { CustomNumberInput } from '@/common/components/AntdForm/AntdFormNumberInput.tsx'
import { FormInput } from '@/common/components/Form/FormInput.tsx'
import { Currency } from '@/common/types/region-config.def.ts'


type FormMultiAdditionalBonusProps = {
  hasCnConditions?: boolean
  name: string
  hasSubjectToPrevIncomeProof?: boolean
  hasSubjectToClawback?: boolean
  hasRepaymentYears?: boolean
  currencyOptions: Currency[]
}

export const FormMultiAdditionalBonus: React.FC<FormMultiAdditionalBonusProps> = ({
  hasCnConditions,
  name,
  hasSubjectToClawback = true,
  hasSubjectToPrevIncomeProof = true,
  hasRepaymentYears = false,
  currencyOptions,
}) => {
  const { fieldName } = useFormSection(name)
  const { control } = useFormContext()


  // Use the useFieldArray hook to manage dynamic form inputs
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${fieldName}.paymentYears`, // Name of the field array in your form data
  })

  const handleRemove = (index: number) => {
    remove(index)
  }

  return (
    <FormSection name={name}>
      <div className="flex flex-col gap-4">
        {hasSubjectToPrevIncomeProof && <FormSwitch name="subjectToPrevIncomeProof" />}
        {hasSubjectToClawback && <FormSwitch name="subjectToClawback" />}
        {hasRepaymentYears && <FormInput name="repaymentTime" />}
        <Button
          onClick={() => append({})}
        >
          Add New Payment
        </Button>
      </div>
      {fields.map((item, index) => (
        <div key={item.id} className={cn('flex flex-col gap-4')}>
          <div className={cn('flex justify-between')}>
            <b>Payment {index + 1}</b>
            <Button danger htmlType="button" onClick={() => handleRemove(index)} icon={<Trash2 />} />
          </div>
          <Controller
            render={({ field }) => <div className="flex flex-col">
              <p>Currency</p>
              <Select
                value={field.value}
                onChange={field.onChange}
                options={currencyOptions.map(x => ({ value: x, label: x }))}
              />
              <SimpleFormMessage name={`${fieldName}.paymentYears.${index}.currency`} />
            </div>}
            key={`${fieldName}.paymentYears.${index}.currency`}
            name={`${fieldName}.paymentYears.${index}.currency`}
            control={control}
          />
          <Controller
            render={({ field }) => <div className="flex flex-col">
              <p>Fiscal Year</p>
              <Input
                value={field.value}
                onChange={field.onChange}
              />
              <SimpleFormMessage name={`${fieldName}.paymentYears.${index}.year`} />
            </div>}
            key={`${fieldName}.paymentYears.${index}.year`}
            name={`${fieldName}.paymentYears.${index}.year`}
            control={control}
          />
          <Controller
            render={({ field }) => <div className="flex flex-col">
              <p>Payout Amount</p>
              <CustomNumberInput value={field.value} onChange={field.onChange} />
              <SimpleFormMessage name={`${fieldName}.paymentYears.${index}.yearPayoutAmount`} />
            </div>}
            key={`${fieldName}.paymentYears.${index}.yearPayoutAmount`}
            name={`${fieldName}.paymentYears.${index}.yearPayoutAmount`}
            control={control}
          />
          <Controller
            render={({ field }) => <div className="flex flex-col">
              <p>Year Payable On</p>
              <Input
                value={field.value}
                onChange={field.onChange}
              />
              <p className="text-sm mt-2 text-[#67748a]">e.g. February 2026</p>
              <SimpleFormMessage name={`${fieldName}.paymentYears.${index}.yearPayableOn`} />
            </div>}
            key={`${fieldName}.paymentYears.${index}.yearPayableOn`}
            name={`${fieldName}.paymentYears.${index}.yearPayableOn`}
            control={control}
          />
          <Controller
            render={({ field }) => <div className="flex flex-col">
              <p>Year Conditions</p>
              <Input.TextArea
                rows={4}
                value={field.value}
                onChange={field.onChange}
                placeholder="on the condition of..."
              />
              <SimpleFormMessage name={`${fieldName}.paymentYears.${index}.yearConditions`} />
            </div>}
            key={`${fieldName}.paymentYears.${index}.yearConditions`}
            name={`${fieldName}.paymentYears.${index}.yearConditions`}
            control={control}
          />
          {hasCnConditions && (
            <Controller
              render={({ field }) => <div className="flex flex-col">
                <p>CN Year Conditions</p>
                <Input
                  value={field.value}
                  onChange={field.onChange}
                />
                <SimpleFormMessage name={`${fieldName}.paymentYears.${index}.cnYearConditions`} />
              </div>}
              key={`${fieldName}.paymentYears.${index}.cnYearConditions`}
              name={`${fieldName}.paymentYears.${index}.cnYearConditions`}
              control={control}
            />
          )}
        </div>
      ))}
    </FormSection>
  )
}
